/**
 * Bulk Assign Units — paste a manifest, get units on a production.
 *
 * The flow is deliberately paste-first: ops receive unit lists as email or
 * manifest text, so the drawer reads codes (and the production, sites and date
 * ranges named around them) straight out of that text, shows what it matched
 * and what it could not, and only then writes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { bulkAssignUnits, createAdminGroup } from '@/lib/adminApi';
import { expandUnitCodeShorthand, formatDayRange, parseDateRange } from '@/lib/unitCodeParser';
import { useBulkAssign } from './useBulkAssign';
import { useBulkAssignData } from './useBulkAssignData';
import { GroupPicker } from './GroupPicker';
import { BatchDateFields } from './BatchDateFields';
import { DropTargets } from './DropTargets';
import { UnitStagingList } from './UnitStagingList';
import { UnmatchedList } from './UnmatchedList';
import { AiSuggestions } from './AiSuggestions';
import { usePasteAssist } from './usePasteAssist';
import { AssignReceipt } from './AssignReceipt';
import { didChange } from './AssignReceipt.helpers';
import { PARENT_GROUP_COLOR, siteColorAt } from './targetStyles';
import { MODEL_LABELS } from '@/lib/constants';
import {
  PARENT_GROUP_TARGET,
  type AssignmentResult,
  type BulkAssignGroup,
  type BulkAssignResponse,
} from './types';

interface BulkAssignDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Units ticked in the fleet table before the drawer opened. */
  prefillUnitIds: string[];
  /** Tenant wording for a parent group — "Production" for film. */
  groupLabel: string;
  statusLabel: (status: string) => string;
  /** Reload the page's own units/groups after a successful write. */
  onAssigned: () => void;
}

type Phase = 'form' | 'committing' | 'receipt';

/** Words that mean a line is about dates, whether or not the rules can read it. */
const DATE_HINT = /\b(?:\d{1,2}(?:st|nd|rd|th)|jan|feb|mar|apr|may|jun|jul|aug|sept?|oct|nov|dec|thru|through|until|till)\b/i;

export function BulkAssignDrawer({
  open,
  onClose,
  prefillUnitIds,
  groupLabel,
  statusLabel,
  onAssigned,
}: BulkAssignDrawerProps) {
  const { units, parentGroups, sites, companies, loading, error, reload } =
    useBulkAssignData(open);
  const [phase, setPhase] = useState<Phase>('form');
  const [receipt, setReceipt] = useState<BulkAssignResponse | null>(null);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCompanyId, setNewCompanyId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [clearArmed, setClearArmed] = useState(false);
  const [localParentGroups, setLocalParentGroups] = useState<BulkAssignGroup[]>([]);

  useEffect(() => {
    if (!newCompanyId && companies.length) setNewCompanyId(companies[0].id);
  }, [companies, newCompanyId]);

  const allParentGroups = useMemo(
    () => [...localParentGroups, ...parentGroups],
    [localParentGroups, parentGroups]
  );

  // The catalogue plus whatever the loaded fleet actually uses: a model added
  // to filter_models but not yet to the static constant must still parse, or
  // its units would not even show up as unmatched.
  const modelCodes = useMemo(
    () => [...new Set([...Object.keys(MODEL_LABELS), ...units.map((u) => u.modelCode)])]
      .filter(Boolean)
      .sort((a, b) => b.length - a.length),
    [units]
  );

  const s = useBulkAssign({
    open,
    units,
    parentGroups: allParentGroups,
    sites,
    modelCodes,
    prefillUnitIds,
    autoActivate: true,
  });

  const assist = usePasteAssist(open);
  // Ask for dates when the rules read none from the paste, or when some line
  // plainly talks about dates ("17th thru 24th") that the rules could not read.
  // Read the same expanded text the hook parses ('1re1 Aug 17' → '1RE001TCA Aug 17'),
  // so a date the rules already read is not asked for again.
  const needDates = useMemo(() => {
    if (!s.rawText.trim()) return false;
    const knownCodes = units.map((u) => u.unitCode);
    const text = expandUnitCodeShorthand(s.rawText, modelCodes, knownCodes).text;
    const opts = { knownCodes };
    if (parseDateRange(text, modelCodes, undefined, opts) === null) return true;
    return text
      .split(/\r?\n/)
      .some((line) => DATE_HINT.test(line) && parseDateRange(line, modelCodes, undefined, opts) === null);
  }, [s.rawText, modelCodes, units]);

  const companyNameFor = useCallback(
    (companyId: string) => companies.find((c) => c.id === companyId)?.name ?? '—',
    [companies]
  );

  const targetParentGroup = allParentGroups.find((p) => p.id === s.targetGroupId);
  const parentGroupName = targetParentGroup?.name ?? '';

  const overrideCount =
    Object.keys(s.modelDates).length +
    Object.keys(s.unitDates).length +
    Object.keys(s.lineDates).length;

  const colorForTarget = useCallback(
    (target: string) => {
      if (target === PARENT_GROUP_TARGET) return PARENT_GROUP_COLOR;
      const index = s.sitesForTarget.findIndex((site) => site.id === target);
      return index === -1 ? PARENT_GROUP_COLOR : siteColorAt(index);
    },
    [s.sitesForTarget]
  );

  const handleCreateGroup = async () => {
    const name = newName.trim();
    if (!name || !newCompanyId) return;
    setIsCreating(true);
    try {
      const { group } = await createAdminGroup({ name, company_id: newCompanyId });
      const created: BulkAssignGroup = {
        id: group.id,
        name: group.name,
        companyId: group.company_id,
        parentGroupId: group.parent_group_id ?? null,
      };
      setLocalParentGroups((prev) => [created, ...prev]);
      s.chooseGroup(created.id);
      setCreatingGroup(false);
      setNewName('');
      reload();
      onAssigned(); // pull the new group into the page's own lists
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : `Could not create the ${groupLabel.toLowerCase()}`
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleCommit = async () => {
    if (!s.targetGroupId || !s.stagedIds.length) return;
    setPhase('committing');
    try {
      // A staged id can go stale if the fleet reloads underneath us; skip
      // rather than dereference undefined inside the commit handler.
      const assignments = s.stagedIds.flatMap((unitId) => {
        const unit = units.find((u) => u.id === unitId);
        if (!unit) return [];
        const target = s.staged[unitId];
        const window = s.effectiveDates(unit);
        return [{
          unitId,
          groupId: target === PARENT_GROUP_TARGET ? s.targetGroupId : target,
          startDate: window.start || null,
          endDate: window.end || null,
        }];
      });
      if (!assignments.length) {
        setPhase('form');
        toast.error('Those units are no longer available — reload and try again.');
        return;
      }

      const response = await bulkAssignUnits({
        assignments,
        setActive: s.setActive,
        ...(assist.provenance ?? {}),
      });
      setReceipt(response);
      setPhase('receipt');
      reload();
      onAssigned();
    } catch (error) {
      setPhase('form');
      toast.error(error instanceof Error ? error.message : 'Assignment failed');
    }
  };

  const handleDone = () => {
    // Count what was actually written: a unit already on the destination can
    // still have been activated or dated, and the server reports that as
    // outcome 'unchanged'.
    const updated = (receipt?.results ?? []).filter(didChange).length;
    if (updated > 0) {
      toast.success(
        `${updated} ${updated === 1 ? 'unit' : 'units'} assigned to ${parentGroupName}`
      );
    }
    setReceipt(null);
    setPhase('form');
    onClose();
  };

  /* ---------- footer ---------- */

  const stagedCount = s.stagedIds.length;

  let primaryLabel: string;
  let primaryAction: (() => void) | null = null;
  if (phase === 'receipt') {
    primaryLabel = 'Done';
    primaryAction = handleDone;
  } else if (phase === 'committing') {
    primaryLabel = 'Assigning…';
  } else if (!s.targetGroupId) {
    primaryLabel = `Pick a ${groupLabel.toLowerCase()}`;
  } else if (!stagedCount) {
    primaryLabel = 'Add units';
  } else {
    primaryLabel = `Assign ${stagedCount} ${stagedCount === 1 ? 'unit' : 'units'}`;
    primaryAction = handleCommit;
  }

  const footerNote =
    phase !== 'form'
      ? ''
      : !s.targetGroupId
        ? stagedCount
          ? `${stagedCount} ${stagedCount === 1 ? 'unit' : 'units'} read · pick a ${groupLabel.toLowerCase()} above`
          : ''
        : [
            `${stagedCount} ${stagedCount === 1 ? 'unit' : 'units'}`,
            formatDayRange(s.batch.start, s.batch.end),
            s.unchangedCount ? `${s.unchangedCount} unchanged` : '',
          ]
            .filter(Boolean)
            .join(' · ');

  const sourceMeta =
    s.source === 'paste'
      ? !s.rawText.trim()
        ? ''
        : s.codes.length === 0
          ? 'No unit codes found'
          : [
              `${s.codes.length} codes`,
              `${s.codes.length - s.unmatched.length} matched`,
              s.duplicates ? `${s.duplicates} duplicate` : '',
            ]
              .filter(Boolean)
              .join(' · ')
      : `${s.pool.length} of ${units.length} units`;

  const footer = (
    <div className="flex items-center gap-3">
      <span className="min-w-0 flex-1 text-xs text-muted-foreground">{footerNote}</span>
      {phase === 'form' && stagedCount > 0 && (
        <button
          type="button"
          onMouseLeave={() => setClearArmed(false)}
          onClick={() => {
            if (!clearArmed) {
              setClearArmed(true);
              return;
            }
            s.reset();
            assist.clear();
            setClearArmed(false);
          }}
          className={`flex h-[38px] flex-none items-center gap-1.5 rounded-md border px-3 text-[13px] transition-colors ${
            clearArmed
              ? 'border-destructive bg-destructive/15 font-medium text-destructive'
              : 'border-border text-muted-foreground hover:text-foreground'
          }`}
        >
          {clearArmed ? 'Confirm' : 'Clear'}
        </button>
      )}
      <button
        type="button"
        onClick={() => primaryAction?.()}
        disabled={!primaryAction}
        className="h-[38px] flex-none rounded-md px-4 text-sm font-medium transition-[filter,background-color] disabled:cursor-not-allowed disabled:bg-accent disabled:text-muted-foreground/80 bg-[#8FB560] text-black hover:brightness-95"
      >
        {primaryLabel}
      </button>
    </div>
  );

  return (
    <AdminFormDrawer
      open={open}
      onClose={onClose}
      title={`Assign to ${groupLabel.toLowerCase()}`}
      widthClassName="w-full sm:w-[540px]"
      footer={footer}
    >
      {phase === 'committing' && (
        <div className="flex items-center justify-center gap-2.5 py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[#8FB560]" />
          <span className="text-[13px] text-muted-foreground">Writing assignments…</span>
        </div>
      )}

      {phase === 'receipt' && receipt && (
        <AssignReceipt
          results={receipt.results}
          warnings={receipt.warnings}
          colorFor={(result: AssignmentResult) =>
            colorForTarget(result.isSite ? (result.toGroupId ?? PARENT_GROUP_TARGET) : PARENT_GROUP_TARGET)
          }
        />
      )}

      {phase === 'form' && loading && units.length === 0 && (
        <div className="flex items-center justify-center gap-2.5 py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[#8FB560]" />
          <span className="text-[13px] text-muted-foreground">Loading fleet…</span>
        </div>
      )}

      {phase === 'form' && error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
          {error}
        </div>
      )}

      {phase === 'form' && !(loading && units.length === 0) && (
        <div className="flex flex-col gap-3.5">
          <GroupPicker
            parentGroups={allParentGroups}
            companies={companies}
            companyNameFor={companyNameFor}
            value={s.targetGroupId}
            onChange={s.chooseGroup}
            needsChoice={stagedCount > 0 && !s.targetGroupId}
            creating={creatingGroup}
            onStartCreate={() => setCreatingGroup(true)}
            onCancelCreate={() => setCreatingGroup(false)}
            newName={newName}
            onNewName={setNewName}
            newCompanyId={newCompanyId}
            onNewCompanyId={setNewCompanyId}
            onCreate={handleCreateGroup}
            isCreating={isCreating}
            groupLabel={groupLabel}
          />

          <BatchDateFields
            batch={s.batch}
            onChange={s.setBatchDate}
            overrideCount={overrideCount}
            activatableCount={s.activatableCount}
            nextFutureStart={s.nextFutureStart}
            setActive={s.setActive}
            onToggleActive={() => s.setSetActive(!s.setActive)}
          />

          {s.targetGroupId && (
            <DropTargets
              parentGroupName={parentGroupName}
              sites={s.sitesForTarget}
              countFor={(target) => s.idsInTarget(target).length}
              idsIn={s.idsInTarget}
              onDropUnits={(ids, target) => s.stage(ids, target)}
              onDragUnits={() => setIsDragging(true)}
              onDragEnd={() => setIsDragging(false)}
              allStagedIds={s.stagedIds}
              isDragging={isDragging}
            />
          )}

          <div className="flex gap-[3px] rounded-md border border-border bg-background p-[3px]">
            {(['paste', 'fleet'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => s.setSource(mode)}
                className={`h-[30px] flex-1 rounded text-xs font-medium transition-colors ${
                  s.source === mode
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {mode === 'paste' ? 'Paste list' : 'Pick from fleet'}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            {s.source === 'paste' ? (
              <textarea
                value={s.rawText}
                onChange={(e) => {
                  s.applyText(e.target.value);
                  // Suggestions were made against the old text.
                  assist.invalidate();
                }}
                placeholder={`Paste a manifest, email, or list\ne.g. Basecamp Alpha: 1RE191TCA, 2RE066TCA\nAug 17 – 24`}
                className="h-[104px] w-full resize-none rounded-md border border-border bg-background px-2.5 py-2 font-mono text-xs leading-relaxed text-foreground outline-none"
              />
            ) : (
              <input
                value={s.search}
                onChange={(e) => s.setSearch(e.target.value)}
                placeholder="Filter units"
                className="h-[38px] w-full rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none"
              />
            )}
            {sourceMeta && (
              <span className="text-xs tabular-nums text-muted-foreground">{sourceMeta}</span>
            )}
          </div>

          <UnitStagingList
            pool={s.pool}
            modelLabels={MODEL_LABELS}
            staged={s.staged}
            targetGroupId={s.targetGroupId}
            parentGroupName={parentGroupName}
            sites={s.sitesForTarget}
            batch={s.batch}
            modelDates={s.modelDates}
            unitDates={s.unitDates}
            lineDates={s.lineDates}
            dateScope={s.dateScope}
            effectiveDates={s.effectiveDates}
            statusLabel={statusLabel}
            onToggleUnit={s.toggleStaged}
            onStageMany={s.stage}
            onUnstageMany={s.unstage}
            onDragUnits={() => setIsDragging(true)}
            onDragEnd={() => setIsDragging(false)}
            onToggleDateScope={s.toggleDateScope}
            onSetScopedDate={s.setScopedDate}
            onClearScopedDate={s.clearScopedDate}
          />

          {s.pool.length === 0 && s.source === 'fleet' && (
            <div className="px-0.5 py-1 text-xs text-muted-foreground">No units match.</div>
          )}

          <UnmatchedList items={s.unmatched} onReplace={s.replaceCode} />

          {s.source === 'paste' && (
            <AiSuggestions
              assist={assist}
              canRequest={Boolean(s.rawText.trim()) && (s.unmatched.length > 0 || needDates)}
              onRequest={() => void assist.request(s.rawText, needDates)}
              onAcceptUnits={(items) => items.forEach((item) => s.adoptCode(item.unit_code, item.evidence))}
              onUseDates={(item) => s.setBatchWindow({ start: item.start, end: item.end })}
            />
          )}
        </div>
      )}
    </AdminFormDrawer>
  );
}
